import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'context/ThemeContext'
import SideNavBar from '../SideNavBar'
import societeaLogoDark from 'assets/societea_logo_avatea_long_932x220_nobg_dark.png'
import societeaLogoLight from 'assets/societea_logo_avatea_long_932x220_nobg.png'
import societeaBrandMark from 'assets/societea_brandmark.svg'

import MuiAppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Avatar from '@mui/material/Avatar'
import Tooltip from '@mui/joy/Tooltip'
import { Settings, LogOut, Menu as MenuIcon, Plus, Bell, Users, ListChecks } from 'lucide-react' 
import IssueSearch from 'components/Issues/IssueSearch'
// <-- NOTE: We can import an X icon from 'lucide-react', or use your own.

import AddIssue from 'components/Issues/AddIssue'
import { useUser } from 'util/UserContext';
import { handleLogout } from 'components/Auth/AuthLogout'
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';
import LoadingSkeleton from 'ui/LoadingSkeleton'
import AddImpact from 'components/Impacts/AddImpact'
import api from 'config/axiosConfig'
import NotificationsModal from '../../Notifications/NotificationsModal'

type Suggestion =
  | {
      type: 'issue';
      id: number;
      name: string;
      subject_properties?: any;
    }
  | {
      type: 'wiki';
      name: string;
      link: string;
      cname: string;
      subject_properties?: any;
    };

const AppBar = () => {
  const { currentUser, setShowAuthModal, userIsMobile, awaitingUser, setShowUserOnboardModal, userOnboardingTasks } = useUser();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openAddIssueModal, setOpenAddIssueModal] = useState(false);
  const [openAddImpactModal, setOpenAddImpactModal] = useState(false);
  const [prepopulateData, setPrepopulateData] = useState<{
    name: string;
    wikipediaLink: string;
    cname: string;
    subject_properties: any;
  } | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const searchInputContainerRef = useRef<HTMLDivElement>(null);

  const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN;

  const UserButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
  >((props, ref) => (<>
    <button
      type="button"
      ref={ref}
      {...props}
      className="flex items-center justify-center"
    >
      <Avatar
        alt={currentUser?.username || 'Profile'}
        src={
          currentUser?.properties_public?.avatar_key
            ? `${IMAGE_CDN}/${currentUser.properties_public.avatar_key}`
            : undefined
        }
        sx={{ width: 32, height: 32 }}
      />
      {(userOnboardingTasks !== null && userOnboardingTasks < 5 ) && ( 
        <span className="size-[12px] absolute -right-[2px] top-[12px] rounded-full bg-eoe-red animate-pulse"/>
      )}
    </button></>
  ))
  UserButton.displayName = 'UserButton'

  const UserOptions = () => (
    <>
      {!userIsMobile ? (
        <div className="mt-2 flex flex-col p-2 min-w-48 space-y-1">
          {userOnboardingTasks !== null && userOnboardingTasks < 5 && (
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            setShowUserOnboardModal(true);
          }}>
            <div className="flex items-center gap-3">
              <ListChecks className="size-5 mr-1"/>
              <span>Enhance your profile</span><span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">({userOnboardingTasks} / 5)</span>
              <span className="size-[12px] rounded-full bg-eoe-red"/>
            </div>
          </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            navigate(`/user/${currentUser?.username}`);
          }} className="px-0">
            <div className="flex items-center gap-2">
              <Avatar
                alt={currentUser?.username || 'Profile'}
                src={
                  currentUser?.properties_public?.avatar_key
                    ? `${IMAGE_CDN}/${currentUser.properties_public.avatar_key}`
                    : undefined
                }
                sx={{ width: 32, height: 32 }}
              />
              <div className="flex flex-col">
                <span className="text-sm">View Profile</span>
                <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{currentUser?.username}</span>
              </div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            navigate('/parteas/member');
          }}>
            <div className="flex items-center gap-3">
              <Users className="size-5 mr-1"/>
              <span>My Parteas</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            handleLogout({ redirectToHome: false });
          }}>
            <div className="flex items-center gap-3">
              <LogOut className="size-5 mr-1"/>
              <span>Log Out</span>
            </div>
          </DropdownMenuItem>
          <hr className="menu-divider w-full border-eoe-border-main dark:border-eoe-dark-border-main my-2"/>
          <DropdownMenuItem onClick={() => {
            setMenuOpen(false);
            navigate('/settings/');
          }}>
            <div className="flex items-center gap-3">
              <Settings className="size-5 mr-1"/>
              <span>Settings</span>
            </div>
          </DropdownMenuItem>
        </div>
      ) : (
        <div className="flex flex-col [&>:not(button):not(hr)]:px-3 [&>:not(button):not(hr)]:py-2">
          {userOnboardingTasks !== null && userOnboardingTasks < 5 && (
            <button 
              onClick={() => {
                setDrawerOpen(false);
                setShowUserOnboardModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
            >
              <ListChecks className="size-5 mr-1.5"/>
              <span>Enhance your profile</span><span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">({userOnboardingTasks} / 5)</span>
            </button>
          )}
          <button 
            onClick={() => {
              setDrawerOpen(false);
              navigate(`/user/${currentUser?.username}`);
            }}
            className="flex items-center gap-2 px-[10px] py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <Avatar
              alt={currentUser?.username || 'Profile'}
              src={
                currentUser?.properties_public?.avatar_key
                  ? `${IMAGE_CDN}/${currentUser.properties_public.avatar_key}`
                  : undefined
              }
              sx={{ width: 32, height: 32 }}
            />
            <div className="flex flex-col text-left">
              <span className="text-sm">View Profile</span>
              <span className="text-xs">{currentUser?.username}</span>
            </div>
          </button>
          <button 
            onClick={() => {
              setDrawerOpen(false);
              navigate('/parteas/member');
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <Users className="size-5 mr-1.5"/>
            <span>My Parteas</span>
          </button>
          <button 
            onClick={() => {
              setDrawerOpen(false);
              handleLogout({ redirectToHome: false });
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <LogOut className="size-5 mr-1.5"/>
            <span>Log Out</span>
          </button>
          <hr className="menu-divider border-eoe-border-main dark:border-eoe-dark-border-main m-2"/>
          <button 
            onClick={() => {
              setDrawerOpen(false);
              navigate('/settings/');
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
          >
            <Settings className="size-5 mr-1.5"/>
            <span>Settings</span>
          </button>
        </div>
      )}
    </>
  )

  // ------
  // Suggestion Handlers
  // ------
  const handleSelectSuggestion = (suggestion: Suggestion) => {
    if (suggestion.type === 'issue') {
      navigate(`/issue/${encodeURIComponent(suggestion.name.replace(/ /g, '_'))}`);
    } else {
      // wiki
      if (awaitingUser) {
        return
      }
      if (!currentUser) {
        setShowAuthModal(true);
      } else {
        setPrepopulateData({
          name: suggestion.name,
          wikipediaLink: suggestion.link,
          cname: suggestion.cname,
          subject_properties: suggestion.subject_properties,
        });
        setOpenAddIssueModal(true);
      }
    }

    setIsInputFocused(false);
  };
  
  const handleAddImpactButtonClick = () => {
    if (awaitingUser) {
      return
    }
    if (!currentUser) {
      setShowAuthModal(true);
    } else if (openAddImpactModal) {
      setOpenAddImpactModal(false);
    } else {
      setOpenAddImpactModal(true);
    }
  };

  const handleLoginButtonClick = () => {
    if (awaitingUser) {
      return
    }
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      navigate('/');
    }
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setIsDrawerOpen(open);
    if (open) {
      document.body.classList.add('drawer-open');
    } else {
      document.body.classList.remove('drawer-open');
    }
  };

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!currentUser) return;
      try {
        const response = await api.get('/api/notifications/unread/count');
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Error fetching unread notifications:', error);
      }
    };

    fetchUnreadCount();
    // Set up polling every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [currentUser]);

  return (
    <div className="dark:bg-eoe-dark-bg">
      <MuiAppBar
        position="fixed"
        variant="outlined"
        elevation={0}
        className="bg-white dark:bg-eoe-dark-bg transition-colors duration-200 dark:border-eoe-dark-border-main"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          border: 'none',
          borderBottom: '0.1rem solid',
          borderColor: 'rgb(200, 200, 200)',
          backgroundColor: 'transparent',
        }}
      >
        <Toolbar disableGutters className="flex mx-2 md:mx-3">
          {/* Left: Menu + Logo */}
          <div className="flex gap-xs shrink-0 items-center justify-start">
            <button
              aria-label="side navigation menu"
              className="hover:bg-gray-300 dark:hover:bg-gray-700 dark:text-gray-300 rounded-full size-8 lg:hidden"
              onClick={toggleDrawer(!isDrawerOpen)}
            >
              <MenuIcon className="size-6 ml-1 text-eoe-text dark:text-eoe-dark-text" />
            </button>
            <Tooltip title="Return to home page" arrow>
              <a href="/" className="no-underline flex items-center">
                <span className="logo-earth-container ml-1 mr-2 flex-1 items-center md:hidden">
                  <img
                    src={societeaBrandMark}
                    alt="Societea Brand Mark"
                    className="logo size-10 bg-white dark:bg-eoe-dark-bg"
                  />
                </span>
                <span className="logo-text-container ml-1 hidden md:flex items-center md:my-2 mr-2">
                  <img
                    src={theme === 'light' ? societeaLogoLight : societeaLogoDark}
                    alt="Societea Logo full"
                    className="logo-text h-10 w-auto"
                  />
                </span>
              </a>
            </Tooltip>
          </div>

          {/* Middle: Search Input */}
          <div
            ref={searchInputContainerRef}
            className="flex flex-col justify-stretch w-full md:block md:pointer-events-auto md:w-[560px] md:mx-auto"
          >
            {/* Wrap the input + 'X' in relative container */}
            <div className="relative">
              <IssueSearch
                onSelect={handleSelectSuggestion}
                placeholder="Search issues..."
                className="w-full"
                autoFocus={false}
              />
            </div>            
          </div>

          {/* Right: Auth / User Menu */}
          <>
          {awaitingUser ? (
            <div className="flex items-center">
              <LoadingSkeleton height="h-9" width="w-9" rounded='rounded-full' margin='ml-1'/>
            </div>
          ) : (!awaitingUser && currentUser ? (
            <div className="flex items-center">
              <button
                className={`rounded-full pr-2 pl-1 py-2 mx-1 justify-center items-center gap-1 text-sm text-eoe-gray dark:text-eoe-dark-text font-semibold hover:bg-eoe-red hover:text-white cursor-pointer transition-colors ${!isInputFocused ? 'flex md:flex' : 'hidden md:flex'}`}
                onClick={handleAddImpactButtonClick}
              >
                <Plus className="w-5 h-5" />
                <span>Impact</span>
              </button>
              <button
                className="rounded-full p-1 mx-1 justify-center items-center text-sm text-eoe-gray dark:text-eoe-dark-text font-semibold hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover cursor-pointer transition-colors"
                onClick={() => setShowNotifications(true)}
              >
                {unreadCount < 1 ? (
                  <Bell className="size-5"/>
                ) : (
                  <span className="items-center">
                    <Bell className="size-6"/>
                    <span className="size-[10px] absolute right-[40px] top-[16px] rounded-full bg-eoe-red"/>
                  </span>
                )}
              </button>
              {openAddImpactModal && (
                <AddImpact
                    onImpactAdded={() => {}}
                    open={openAddImpactModal}
                    onClose={() => setOpenAddImpactModal(false)}
                />
              )}
              {openAddIssueModal && (
                <AddIssue
                  open={openAddIssueModal}
                  onClose={() => setOpenAddIssueModal(false)}
                  prepopulateData={prepopulateData}
                />
              )}
              <div className={`flex-grow-0 ${!isInputFocused ? '' : 'ml-2 md:ml-0'}`}>
                {userIsMobile ? (
                  <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                    <DrawerTrigger asChild>
                      <UserButton />
                    </DrawerTrigger>
                    <DrawerContent title='User Menu'>
                      <UserOptions />
                    </DrawerContent>
                  </Drawer>
                ) : (
                  <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                    <DropdownMenuTrigger asChild>
                      <UserButton />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <UserOptions />
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ) : (
            <Tooltip title="Login or sign up" arrow>
              <button
                className="rounded-3xl px-4 py-2 mx-2 text-sm whitespace-nowrap dark:text-eoe-dark-text bg-eoe-red hover:bg-eoe-red-hov text-white cursor-pointer transition-colors"
                onClick={handleLoginButtonClick}
              >
                Log In
              </button>
              </Tooltip>
            ))}
          </>
        </Toolbar>
      </MuiAppBar>
      <SideNavBar isOpen={isDrawerOpen} toggleDrawer={toggleDrawer} onClick={() => setIsDrawerOpen(false)} />
      {showNotifications && (
        <NotificationsModal
          onClose={() => setShowNotifications(false)}
          onNotificationsRead={(count) => setUnreadCount(prev => Math.max(0, prev - count))}
        />
      )}
    </div>
  );
};

export default AppBar;
